import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CalendarDays, ClipboardList, Gavel, Loader2, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { compactText, formatLegalDate, getHealthTone, getStatusTone } from '@/lib/legalOs';
import { loadBoardSummary } from '@/lib/workflowData';
import { AppEmptyState, AppMetricCard, AppPage, AppPageHeader, AppSectionHeader } from '@/components/layout/AppPage';
import WorkflowCompatibilityNotice from '@/components/layout/WorkflowCompatibilityNotice';

function SurfaceCard({ title, children, action }) {
  return (
    <section
      className="advoq-card"
      style={{
        padding: '18px',
        borderRadius: '24px',
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        display: 'grid',
        gap: '14px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '17px' }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function BoardPage() {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadNotice, setLoadNotice] = useState('');

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data, notice } = await loadBoardSummary({
        apiBase: API,
        headers: getAuthHeaders(),
        user,
      });
      setSummary(data);
      setLoadNotice(notice);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, user]);

  useEffect(() => {
    document.title = 'Board / Court Watch — Advoq';
    loadSummary();
  }, [loadSummary]);

  const metrics = useMemo(() => ([
    {
      icon: Gavel,
      label: 'Court-watch items',
      value: summary?.court_watch_items?.length || 0,
      note: 'Listings, portal checks, and court-facing movement.',
      accent: '#2563eb',
    },
    {
      icon: CalendarDays,
      label: 'Morning briefs',
      value: summary?.morning_briefs?.length || 0,
      note: 'Matters that already need a hearing-day brief.',
      accent: '#0f766e',
    },
    {
      icon: ClipboardList,
      label: 'Recovery tasks',
      value: summary?.post_hearing_recovery?.length || 0,
      note: 'What must happen after the court-facing move.',
      accent: '#b45309',
    },
    {
      icon: AlertTriangle,
      label: 'Urgent matters',
      value: summary?.dashboard?.urgent_count || 0,
      note: 'Priority matters still leading the board.',
      accent: '#dc2626',
    },
  ]), [summary]);

  return (
    <main className="app-main" data-testid="board-page">
      <AppPage>
        <AppPageHeader
          eyebrow="Board / Court Watch"
          title={summary?.headline || 'Board / Court Watch'}
          description="Cause-list awareness, next-date pressure, order deltas, morning brief posture, and post-hearing recovery in one view."
          actions={(
            <button type="button" className="app-chat-button" onClick={loadSummary} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
              Refresh
            </button>
          )}
          meta={(
            <div className="app-chip-row">
              {summary?.role?.label ? <span className="app-page-chip">{summary.role.label}</span> : null}
              <span className="app-page-chip">{summary?.dashboard?.hearing_this_week || 0} hearings this week</span>
            </div>
          )}
        />
        <WorkflowCompatibilityNotice message={loadNotice} />

        <div className="app-responsive-metrics-grid">
          {metrics.map((item) => (
            <AppMetricCard key={item.label} {...item} />
          ))}
        </div>

        {!loading && !(summary?.matter_boards || []).length ? (
          <AppEmptyState
            icon={Gavel}
            title="No matters are feeding the board yet"
            description="As hearings, listings, and matter boards become active, court-watch and recovery loops will appear here."
          />
        ) : null}

        <div className="app-split-grid">
          <SurfaceCard title="Morning brief" action={<button type="button" className="app-chat-button" onClick={() => navigate('/hearings')}>Open hearings <ArrowRight size={14} /></button>}>
            {(summary?.morning_briefs || []).length ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                {summary.morning_briefs.map((item) => (
                  <button
                    key={`${item.matter_id}_${item.hearing_date}`}
                    type="button"
                    onClick={() => navigate(`/war-room/${item.matter_id}`)}
                    className="advoq-card"
                    style={{
                      textAlign: 'left',
                      padding: '16px',
                      borderRadius: '20px',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--bg-elevated)',
                      display: 'grid',
                      gap: '8px',
                    }}
                  >
                    <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>{item.matter_title}</div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
                      {compactText(item.summary, 160)}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '12px' }}>
                      <span>{item.court_name || 'Court to confirm'}</span>
                      <span>{formatLegalDate(item.hearing_date)}</span>
                      <span>{item.readiness_label || 'Readiness building'}</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                Morning briefs will appear once hearing-facing matters are visible on the board.
              </div>
            )}
          </SurfaceCard>

          <SurfaceCard title="Court watch">
            {(summary?.court_watch_items || []).length ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                {summary.court_watch_items.map((item, index) => {
                  const tone = getStatusTone(item.status);
                  return (
                    <div
                      key={`${item.matter_id}_${item.title}_${index}`}
                      className="advoq-card"
                      style={{
                        padding: '14px',
                        borderRadius: '18px',
                        border: '1px solid var(--border-subtle)',
                        background: 'var(--bg-elevated)',
                        display: 'grid',
                        gap: '8px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                        <div>
                          <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>{item.title}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
                            {compactText(item.description, 160)}
                          </div>
                        </div>
                        <span className="app-page-chip" style={{ background: tone.background, color: tone.color, border: `1px solid ${tone.border}` }}>
                          {item.status || 'watch'}
                        </span>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{item.matter_title}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                Court-watch items are not visible yet.
              </div>
            )}
          </SurfaceCard>
        </div>

        <section style={{ display: 'grid', gap: '16px' }}>
          <AppSectionHeader
            eyebrow="Recovery"
            title="Post-hearing recovery"
            description="The follow-ups that should happen after the listing, hearing, or order movement."
          />
          {(summary?.post_hearing_recovery || []).length ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
              {summary.post_hearing_recovery.map((item, index) => (
                <div
                  key={`${item.matter_id}_${item.title}_${index}`}
                  className="advoq-card"
                  style={{
                    padding: '16px',
                    borderRadius: '20px',
                    border: '1px solid var(--border-subtle)',
                    background: 'var(--bg-surface)',
                    display: 'grid',
                    gap: '8px',
                  }}
                >
                  <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 700 }}>
                    {item.title}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    {item.matter_title}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <AppEmptyState
              icon={ClipboardList}
              title="No recovery tasks are leading"
              description="As hearing boards accumulate next steps, the recovery queue will appear here."
            />
          )}
        </section>

        {(summary?.matter_boards || []).length ? (
          <section style={{ display: 'grid', gap: '16px' }}>
            <AppSectionHeader
              eyebrow="Matter boards"
              title="Court-facing matter posture"
              description="The matters that already have court watch, hearing readiness, and filing posture worth checking."
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
              {summary.matter_boards.map((board) => {
                const tone = getHealthTone(board.health_score || 0);
                return (
                  <button
                    key={board.matter_id}
                    type="button"
                    onClick={() => navigate(`/war-room/${board.matter_id}`)}
                    className="advoq-card"
                    style={{
                      textAlign: 'left',
                      padding: '18px',
                      borderRadius: '22px',
                      border: '1px solid var(--border-subtle)',
                      background: 'var(--bg-surface)',
                      display: 'grid',
                      gap: '10px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '15px', fontWeight: 700 }}>{board.title}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginTop: '6px' }}>
                          {compactText(board.overview, 130)}
                        </div>
                      </div>
                      <span className="app-page-chip" style={{ background: tone.background, color: tone.color, border: `1px solid ${tone.border}` }}>
                        {tone.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: '12px' }}>
                      <span>{board.court_name || 'Court to confirm'}</span>
                      <span>{board.next_hearing_date ? formatLegalDate(board.next_hearing_date) : 'No date'}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}
      </AppPage>
    </main>
  );
}
